import React, {useContext, useEffect, useState} from "react";
import {
    Avatar,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItem,
    Menu,
    MenuItem,
    TextField,
    Typography,
    List,
    Checkbox,
    Divider
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import currencyList from "../../../sampleData/currencyList";
import {formatName, stringAvatar, stringToColor} from "../../Helpers/TextStyling";
import {PAContext} from "../../../Services/PAContext";
import './activities.css';
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";

function ActivityDialogEditCosts({ isOpen, onClose, eventActivity, setEventActivity }) {

    const [anchorChangeCurrencyEl, setAnchorChangeCurrencyEl] = useState(null);

    const { PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray } = useContext(PAContext);
    const { SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;
        getAllContactsFromDatabase()
            .then((contacts) => {
                if (cancelled) return;
                setDbContacts(contacts || []);
            })
            .catch((err) => {
                console.error("Failed to load local contacts database reference context:", err);
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [attendeesReferenceArray, setAttendeesReferenceArray] = useState(authenticated ? dbContacts : SampleAttendees);

    useEffect(() => {
        setAttendeesReferenceArray(authenticated ? dbContacts : SampleAttendees);
    }, [authenticated, dbContacts, SampleAttendees]);

    const [activityCost, setActivityCost] = useState(parseFloat(eventActivity.amount_total) || 0);
    const [activityCurrency, setActivityCurrency] = useState(eventActivity.currency || "USD");
    const [activityCostDistributionType, setActivityCostDistributionType] = useState(eventActivity.assign_equal? "even":"custom");
    const [activityAttendees, setActivityAttendees] = useState(eventActivity.attendees || []);
    const [editModeMap, setEditModeMap] = useState({});

    useEffect(() => {
        if (isOpen && eventActivity) {
            const initialCost = parseFloat(eventActivity.amount_total) || 0;
            const isEvenSplit = eventActivity.assign_equal === true || eventActivity.assign_equal === "true";
            const initialDistribution = isEvenSplit ? "even" : "custom";
            const initialAttendees = eventActivity.attendees || [];

            setActivityCurrency(eventActivity.currency || "USD");
            setActivityCostDistributionType(initialDistribution);
            setActivityAttendees(initialAttendees);

            if (initialCost === 0 && initialDistribution === "custom") {
                const calculatedTotal = initialAttendees.reduce((total, attendee) => {
                    const parsedCost = parseFloat(attendee.cost);
                    return total + (isNaN(parsedCost) ? 0 : parsedCost);
                }, 0);
                setActivityCost(calculatedTotal === 0 ? 0 : Number(calculatedTotal.toFixed(2)));
            } else {
                setActivityCost(initialCost);
            }
        }
    }, [isOpen, eventActivity]);

    const handleOpenCurrencyMenu = (event) => setAnchorChangeCurrencyEl(event.currentTarget);
    const handleCloseCurrencyMenu = () => setAnchorChangeCurrencyEl(null);
    const handleSelectCurrency = (currencyCode) => {
        setActivityCurrency(currencyCode);
        handleCloseCurrencyMenu();
    };

    const distributeEvenlyHelper = (totalAmount, attendeesList) => {
        const count = attendeesList.length;
        if (count === 0) return attendeesList;

        const standardSplit = Math.floor((totalAmount / count) * 100) / 100;
        const totalDistributedBaseline = standardSplit * count;
        const leftoverCents = Math.round((totalAmount - totalDistributedBaseline) * 100);

        const globalHosts = PA_GlobalObject?.Planning?.host || [];
        const primaryHostId = globalHosts[0];

        let hostIndex = -1;
        if (primaryHostId) {
            hostIndex = attendeesList.findIndex(att => att.id === primaryHostId);
        }

        if (hostIndex === -1) {
            hostIndex = 0;
        }

        return attendeesList.map((att, index) => {
            const calculatedCost = index === hostIndex
                ? (standardSplit + (leftoverCents / 100)).toFixed(2)
                : standardSplit.toFixed(2);

            return {
                ...att,
                cost: calculatedCost,
                business_expense_amount: att.is_business_expense ? calculatedCost : (att.business_expense_amount || 0)
            };
        });
    };

    const handleCostChange = (e) => {
        const val = e.target.value;
        setActivityCost(val);

        const parsedTotal = parseFloat(val) || 0;
        if (activityCostDistributionType === "even") {
            setActivityAttendees(prev => distributeEvenlyHelper(parsedTotal, prev));
        }
    };

    const handleDistributionTypeChange = (type) => {
        setActivityCostDistributionType(type);

        if (type === "even") {
            const parsedTotal = parseFloat(activityCost) || 0;
            setActivityAttendees(prev => distributeEvenlyHelper(parsedTotal, prev));
        }
    };

    const handleAttendeeCostChange = (attendeeId, val) => {
        setActivityAttendees(prev => {
            const updatedAttendees = prev.map(att => {
                if (att.id === attendeeId) {
                    return { ...att, cost: val };
                }
                return att;
            });

            const newCalculatedTotal = updatedAttendees.reduce((total, attendee) => {
                const parsedCost = parseFloat(attendee.cost);
                return total + (isNaN(parsedCost) ? 0 : parsedCost);
            }, 0);

            setActivityCost(newCalculatedTotal === 0 ? 0 : Number(newCalculatedTotal.toFixed(2)));
            return updatedAttendees;
        });
    };

    const handleAssignTotalToOnePerson = (targetAttendeeId) => {
        let totalAmount = parseFloat(activityCost);

        const targetAttendee = activityAttendees.find(att => att.id === targetAttendeeId);
        const existingIndividualCost = parseFloat(targetAttendee?.cost);

        if ((isNaN(totalAmount) || totalAmount === 0) && !isNaN(existingIndividualCost) && existingIndividualCost > 0) {
            totalAmount = existingIndividualCost;
            setActivityCost(Number(totalAmount.toFixed(2)));
        } else if ((isNaN(totalAmount) || totalAmount === 0) && activityCostDistributionType === "even") {
            const fallbackEvenAmount = (parseFloat(activityCost) || 0) / (activityAttendees.length || 1);
            totalAmount = fallbackEvenAmount * activityAttendees.length;
        }

        if (isNaN(totalAmount)) totalAmount = 0;

        setActivityCostDistributionType("custom");

        setActivityAttendees(prev => prev.map(att => {
            const finalCost = att.id === targetAttendeeId ? totalAmount.toFixed(2) : "0";
            return {
                ...att,
                cost: finalCost,
                business_expense_amount: att.is_business_expense ? finalCost : 0
            };
        }));
    };

    // ─── PER-ATTENDEE COMPANY COVERED (BUSINESS EXPENSE) LOGIC ────────────
    const handleBusinessExpenseToggle = (attendeeId, isChecked) => {
        setActivityAttendees(
            activityAttendees.map((att) =>
                att.id === attendeeId
                    ? {
                        ...att,
                        is_business_expense: isChecked,
                        business_expense_amount: isChecked ? att.cost : 0,
                    }
                    : att
            )
        );
    };

    const handleBusinessExpenseAmountEdit = (attendeeId) => {
        setEditModeMap((prev) => ({
            ...prev,
            [attendeeId]: !prev[attendeeId],
        }));
    };

    const handleBusinessExpenseAmountChange = (attendeeId, e) => {
        const inputValue = e.target.value;
        setActivityAttendees(
            activityAttendees.map(attendee => {
                if (attendee.id === attendeeId) {
                    return { ...attendee, business_expense_amount: inputValue };
                }
                return attendee;
            })
        );
    };

    const handleBusinessExpenseEnableOnEdit = (attendeeId) => {
        setActivityAttendees(
            activityAttendees.map((att) =>
                att.id === attendeeId
                    ? { ...att, is_business_expense: true }
                    : att
            )
        );
    };

    function saveEventActivitySettings() {
        let updatedAttendees = [...activityAttendees];
        const totalCostNum = parseFloat(activityCost) || 0;
        const isEvenSplit = activityCostDistributionType === "even";

        if (isEvenSplit) {
            updatedAttendees = distributeEvenlyHelper(totalCostNum, updatedAttendees);
        } else {
            updatedAttendees = updatedAttendees.map(att => {
                const parsed = parseFloat(att.cost);
                return {
                    ...att,
                    cost: isNaN(parsed) ? "0" : parsed.toFixed(2)
                };
            });
        }

        const updatedActivity = {
            ...eventActivity,
            amount_total: totalCostNum.toString(),
            assign_equal: isEvenSplit,
            currency: activityCurrency,
            attendees: updatedAttendees
        };

        setEventActivity(updatedActivity);

        onClose();
    }

    // Fixed math precision calculation
    const assignedTotal = activityAttendees.reduce((total, attendee) => {
        const cost = parseFloat(attendee.cost);
        return total + (isNaN(cost) ? 0 : cost);
    }, 0);
    const balanceRemaining = parseFloat((Math.max(0, (parseFloat(activityCost) || 0) - assignedTotal)).toFixed(2));
    const isUnassigned = balanceRemaining > 0.009;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 350,
                    maxWidth: 480,
                    width: '100%',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: '#1e293b',
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    pt: 3,
                    pb: 1
                }}
            >
                Activity Expense Settings
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 16, top: 16, color: '#64748b' }}
            >
                <i className="icon-cross" style={{ fontSize: '14px' }} />
            </IconButton>

            <DialogContent sx={{ px: 3, pb: 2 }}>

                <Box
                    sx={{
                        p: 2,
                        mb: 2.5,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.45)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)',
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center'
                    }}
                >
                    <TextField
                        label="Total Activity Cost"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={activityCost}
                        onChange={handleCostChange}
                        InputProps={{
                            sx: { borderRadius: '12px', background: '#fff', fontWeight: 700 }
                        }}
                    />

                    <Button
                        variant="outlined"
                        onClick={handleOpenCurrencyMenu}
                        sx={{
                            borderRadius: '12px',
                            minHeight: '40px',
                            borderColor: '#e2e8f0',
                            color: '#475569',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { borderColor: '#cbd5e1', bg: '#f8fafc' }
                        }}
                    >
                        {activityCurrency} ▾
                    </Button>

                    <Menu
                        anchorEl={anchorChangeCurrencyEl}
                        open={Boolean(anchorChangeCurrencyEl)}
                        onClose={handleCloseCurrencyMenu}
                        PaperProps={{ sx: { maxHeight: 200, borderRadius: '12px' } }}
                    >
                        {currencyList.map((currency) => (
                            <MenuItem
                                key={currency.code}
                                onClick={() => handleSelectCurrency(currency.code)}
                                selected={currency.code === activityCurrency}
                            >
                                {currency.code} - {currency.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                <Box display="flex" justifyContent="center" mb={2.5}>
                    <ButtonGroup
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            '& .MuiButtonGroup-grouped': { border: '1px solid #e2e8f0 !important' }
                        }}
                    >
                        <Button
                            onClick={() => handleDistributionTypeChange("even")}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '13px',
                                color: activityCostDistributionType === "even" ? '#fff' : '#64748b',
                                background: activityCostDistributionType === "even" ? 'linear-gradient(135deg, #f09637 0%, #f2a654 100%)' : '#fff',
                                '&:hover': { background: activityCostDistributionType === "even" ? '#e08526' : '#f8fafc' }
                            }}
                        >
                            Split Evenly
                        </Button>
                        <Button
                            onClick={() => handleDistributionTypeChange("custom")}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '13px',
                                color: activityCostDistributionType === "custom" ? '#fff' : '#64748b',
                                background: activityCostDistributionType === "custom" ? 'linear-gradient(135deg, #f09637 0%, #f2a654 100%)' : '#fff',
                                '&:hover': { background: activityCostDistributionType === "custom" ? '#e08526' : '#f8fafc' }
                            }}
                        >
                            Custom Assignment
                        </Button>
                    </ButtonGroup>
                </Box>

                <Box
                    sx={{
                        maxHeight: '260px',
                        overflowY: 'auto',
                        px: 0.5,
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' }
                    }}
                >
                    <List dense disablePadding>
                        {activityAttendees.map((_a) => {
                            const attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === _a.id);
                            const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === _a.id);
                            if (!attendee) return null;

                            const fullName = attendee.nickname?.trim()
                                ? `${formatName(attendee.nickname)}`
                                : `${formatName(attendee.fname)} ${formatName(attendee.lname)}`;

                            const customAvatarStyles = {
                                border: `1.5px solid ${stringToColor(fullName)}`,
                                width: 32,
                                height: 32,
                                fontSize: '11px'
                            };

                            return (
                                <Box
                                    key={_a.id}
                                    sx={{
                                        mb: 1.5,
                                        p: 1.5,
                                        borderRadius: '14px',
                                        background: '#fff',
                                        border: '1px solid #f1f5f9',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.015)'
                                    }}
                                >
                                    {/* Primary Row */}
                                    <ListItem
                                        disablePadding
                                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                            {!attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                                <Avatar
                                                    {...stringAvatar(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}
                                                    sx={customAvatarStyles}
                                                />
                                            ) : (
                                                <Avatar
                                                    src={attendeeAvatar.avatar.preview}
                                                    sx={customAvatarStyles}
                                                />
                                            )}
                                            <ListItemText
                                                primary={fullName}
                                                primaryTypographyProps={{ fontWeight: 700, fontSize: '13px', color: '#334155', noWrap: true }}
                                            />
                                        </Box>

                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => handleAssignTotalToOnePerson(_a.id)}
                                                sx={{
                                                    fontSize: '11px',
                                                    fontWeight: 800,
                                                    textTransform: 'none',
                                                    color: '#f09637',
                                                    borderRadius: '8px',
                                                    px: 1,
                                                    minWidth: 'auto',
                                                    '&:hover': { background: 'rgba(240,150,55,0.08)' }
                                                }}
                                            >
                                                Pay All
                                            </Button>

                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                disabled={activityCostDistributionType === "even"}
                                                value={_a.cost || ""}
                                                onChange={(e) => handleAttendeeCostChange(_a.id, e.target.value)}
                                                InputProps={{
                                                    endAdornment: <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700, ml: 0.5 }}>{activityCurrency}</Typography>
                                                }}
                                                sx={{
                                                    width: '95px',
                                                    '& .MuiInputBase-root': { borderRadius: '10px', height: '34px', fontSize: '13px', fontWeight: 700 }
                                                }}
                                            />
                                        </Box>
                                    </ListItem>

                                    {/* Company Covered Row */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            pt: 0.5
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                                            <Checkbox
                                                checked={_a.is_business_expense || false}
                                                onChange={(e) => handleBusinessExpenseToggle(_a.id, e.target.checked)}
                                                sx={{ padding: 0, marginRight: '10px', color: '#cbd5e1', '&.Mui-checked': { color: '#f09637' } }}
                                            />
                                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', lineHeight: 1.2 }}>
                                                Business Expense
                                            </Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ flexGrow: 1, marginRight: '10px' }}>
                                            {editModeMap[_a.id] ? (
                                                <TextField
                                                    label="Amount"
                                                    value={_a.business_expense_amount === 0 ? "" : _a.business_expense_amount}
                                                    onChange={(e) => handleBusinessExpenseAmountChange(_a.id, e)}
                                                    variant="standard"
                                                    onFocus={() => handleBusinessExpenseEnableOnEdit(_a.id)}
                                                    sx={{
                                                        minWidth: '80px',
                                                        maxWidth: '100px',
                                                        '& .MuiInputBase-input': { textAlign: 'right', fontSize: '12px', fontWeight: 700 }
                                                    }}
                                                />
                                            ) : (
                                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>
                                                    {parseFloat(_a.business_expense_amount || 0).toFixed(2)}&nbsp;{activityCurrency}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center">
                                            {editModeMap[_a.id] ? (
                                                <i
                                                    className='icon-check'
                                                    style={{ cursor: 'pointer', fontSize: '14px', color: '#10b981' }}
                                                    onClick={() => handleBusinessExpenseAmountEdit(_a.id)}
                                                />
                                            ) : (
                                                <i
                                                    className='icon-pencil'
                                                    style={{ cursor: 'pointer', fontSize: '12px', color: '#94a3b8' }}
                                                    onClick={() => handleBusinessExpenseAmountEdit(_a.id)}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mt: 1.5, borderColor: '#f1f5f9' }} />
                                </Box>
                            );
                        })}
                    </List>
                </Box>

                {activityCostDistributionType === "custom" && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: '12px',
                            background: isUnassigned ? 'rgba(239, 68, 68, 0.05)' : 'rgba(16, 185, 129, 0.05)',
                            border: isUnassigned ? '1px dashed rgba(239, 68, 68, 0.25)' : '1px dashed rgba(16, 185, 129, 0.25)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: isUnassigned ? '#ef4444' : '#10b981' }}>
                            {isUnassigned ? "Remaining Unassigned:" : "Fully Distributed!"}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 800, color: isUnassigned ? '#ef4444' : '#10b981' }}>
                            {balanceRemaining.toFixed(2)} {activityCurrency}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: '0 24px 24px 24px', gap: 1 }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={saveEventActivitySettings}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #f09637 0%, #f2a654 100%)',
                        boxShadow: '0 4px 14px rgba(240, 150, 55, 0.3)',
                        borderRadius: '10px',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #e08526 0%, #f09637 100%)',
                        }
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ActivityDialogEditCosts;